const DEBUGGING: boolean = process.env.NODE_ENV === 'development'

import { BaseObject, MongoObject } from './_baseClasses'
import { Moment } from './Moment';
import { Auction, auctionStatus } from './Auction';
import * as utils_topshot from '@/utils_topshot';

const ASP_SNAPSHOT_DATE3: Date = new Date('2022-05-12T00:42:15.539Z')       // to sync with Top Shot historical actual backfill

export type sortOrderTypes = 'FMV_DESC' | 'FMV_ASC' | 'AcquiredPrice_DESC' | 'AcquiredPrice_ASC' | 'AcquiredDate_DESC' | 'AcquiredDate_ASC' | 'Movement_DESC' | 'Movement_ASC' | 'CS_DESC' | 'CS_ASC' | 'Player_ASC' | 'Team_ASC' | 'Gain_ASC' | 'Gain_DESC' | 'Serial_ASC' | 'Serial_DESC'

export enum acquiredType {
  Purchase = 1, Gift = 2, Pack = 3, Reward = 4, Unknown = 5, TopShotInternal = 6, Auction = 7, TradedInForTicket = 8, PutIntoPack = 9, Destroyed = 10
}

interface CollectorScore {
  baseline: number,
  challengeCompletionBonus: number,
  setBonus: number,
  seriesTeamBonus: number,
  fullTeamBonus: number,
}

// April 2, 2022
interface CollectorScore2 {
  baseline: number,           // raw fandom score
  challengeCompletionBonus: number,
  setBonus: number,
  seriesTeamBonus: number,
  fullTeamBonus: number,
  historicFullTeamBonus: number,  
  badgeBonus: number,         // e.g. three-star rookies get 500 points
  marketSpendBonus: number,   // 10 points for every $ spent on marketplace to acquire this moment
}

export interface CollectorScore2b {
  mpsScore: number,      // 10 x market spend
  aspScore: number,      // 10 x ASP at time of acquisition or mbb press
  aspWhen: Date,
  overrideScore: number, // only CS can change this
  score: number          // should be just the greater of mpsScore and aspScore
}

export class MomentOwned extends Moment {
  public acquiredDate: Date = null
  public acquiredType: acquiredType = null
  public buyPrice: number = null
  public sellerAddress: string = null
  public sellerUsername: string = null
  public sellerImageURL: string = null
  public collectorScore: CollectorScore = null
  public collectorScore2: CollectorScore2 = null
  public collectorScore2b: CollectorScore2b = null
  public heldKeyDate: Date = null

  // Aug 4, 2022 - locking stuff
  public owner: string = null
  public locked: boolean = null
  public lockDate: Date = null
  public lockExpiryDate: Date = null

  // complex, class-based fields (require to be hydrated after loading from external sources like mongo or elastic)

  // client only, filled in by PortfolioComponent.vue by making a separate REST call to get auctions.
  //  we populate this on the client side rather than on server side so that portofolio can be cached independently of the auction data
  public auction: Auction = null

  constructor(po?: object) {
    super(po)
    if (po) this.injectPlainObject(po)

    this.hydrate()
  }

  protected hydrate(): void {
    super.hydrate()
    if (typeof this.acquiredDate === 'string') this.acquiredDate = new Date(this.acquiredDate)
    if (this.collectorScore2b && typeof this.collectorScore2b.aspWhen === 'string') this.collectorScore2b.aspWhen = new Date(this.collectorScore2b.aspWhen)
    if (typeof this.lockDate === 'string') this.lockDate = new Date(this.lockDate)
    if (typeof this.lockExpiryDate === 'string') this.lockExpiryDate = new Date(this.lockExpiryDate)
  }

  public isSafe(): boolean {
    if (!super.isSafe()) return false
    return true
  }
  public getNiceGainString(): string {
    const numericalGain: number = this.valueFMV - this.buyPrice
    const nice: string = `${numericalGain < 0 ? '-' : '+'}${utils_topshot.getNicePriceString(Math.abs(numericalGain))}`
    return nice
  }
  public getGainClass(): string {
    if (this.valueFMV >= this.buyPrice) return 'positive'
    return 'negative'
  }
  public getNiceAcquiredTypeString(): string {
    switch (this.acquiredType) {
      case acquiredType.Purchase: return "Marketplace"
      case acquiredType.Gift: return "Gift"
      case acquiredType.Pack: return "Pack"
      case acquiredType.Reward: return "Reward"
      case acquiredType.Unknown: return "Gift/Pack"
      case acquiredType.TopShotInternal: return "TS Internal"   // not used
      case acquiredType.Auction: return "Auction"
      case acquiredType.TradedInForTicket: return "Trade In"
      case acquiredType.PutIntoPack: return "Put into Pack"     // not used
      case acquiredType.Destroyed: return "Destroyed"           // not used
    }
    return ""
  }

  public getTotalCollectorScore(): number {
    if (!this.collectorScore) return 0            // should never happen
    return this.collectorScore.baseline + this.collectorScore.challengeCompletionBonus + this.collectorScore.setBonus + this.collectorScore.seriesTeamBonus + this.collectorScore.fullTeamBonus
  }

  // unused
  public getCollectorScoreClassName(): string {
    let numBonuses: number = 0
    if (this.collectorScore) {
      if (this.collectorScore.challengeCompletionBonus) numBonuses++
      if (this.collectorScore.setBonus) numBonuses++
      if (this.collectorScore.seriesTeamBonus) numBonuses++
      if (this.collectorScore.fullTeamBonus) numBonuses++
    }
    if (numBonuses === 0) return 'common'
    if (numBonuses === 1) return 'limitedEdition'
    if (numBonuses === 2) return 'rare'
    if (numBonuses === 3) return 'legendary'
    if (numBonuses >= 4) return 'ultimate'
  }

  public getCollectorScoreOpacity(): number {
    let numBonuses: number = 0
    if (this.collectorScore) {
      if (this.collectorScore.challengeCompletionBonus) numBonuses++
      if (this.collectorScore.setBonus) numBonuses++
      if (this.collectorScore.seriesTeamBonus) numBonuses++
      if (this.collectorScore.fullTeamBonus) numBonuses++
    }
    if (numBonuses === 0) return 0.5
    if (numBonuses === 1) return 0.75
    if (numBonuses >= 2) return 1
  }

  public getCollectionScoreHTML(): string {
    if (!this.collectorScore) return ''           // should never happen
    let html = `<div class="csLine"><b>Baseline:</b> <span class="csValue">${this.collectorScore.baseline}</span></div>`
    if (this.collectorScore.challengeCompletionBonus) html += `<div class="csLine"><b>Challenge Completion:</b> <span class="csValue">${this.collectorScore.challengeCompletionBonus}</span></div>`
    if (this.collectorScore.setBonus) html += `<div class="csLine"><b>Set Completion:</b> <span class="csValue">${this.collectorScore.setBonus}</span></div>`
    if (this.collectorScore.seriesTeamBonus) html += `<div class="csLine"><b>Series Team Completion:</b> <span class="csValue">${this.collectorScore.seriesTeamBonus}</span></div>`
    if (this.collectorScore.fullTeamBonus) html += `<div class="csLine"><b>Full Team Completion:</b> <span class="csValue">${this.collectorScore.fullTeamBonus}</span></div>`
    return html
  }

  public getTotalCollectorScore2(): number {
    if (!this.collectorScore2) return 0            // should never happen
    return this.collectorScore2.baseline + this.collectorScore2.challengeCompletionBonus + this.collectorScore2.setBonus + this.collectorScore2.seriesTeamBonus + this.collectorScore2.fullTeamBonus + this.collectorScore2.historicFullTeamBonus + this.collectorScore2.badgeBonus + this.collectorScore2.marketSpendBonus
  }

  // unused
  public getCollectorScoreClassName2(): string {
    let numBonuses: number = 0
    if (this.collectorScore2) {
      if (this.collectorScore2.challengeCompletionBonus) numBonuses++
      if (this.collectorScore2.setBonus) numBonuses++
      if (this.collectorScore2.seriesTeamBonus) numBonuses++
      if (this.collectorScore2.fullTeamBonus) numBonuses++
      if (this.collectorScore2.historicFullTeamBonus) numBonuses++
      if (this.collectorScore2.badgeBonus) numBonuses++
      // if (this.collectorScore2.marketSpendBonus) numBonuses++
    }
    if (numBonuses === 0) return 'common'
    if (numBonuses === 1) return 'limitedEdition'
    if (numBonuses === 2) return 'rare'
    if (numBonuses === 3) return 'legendary'
    if (numBonuses >= 4) return 'ultimate'
  }

  public getCollectorScoreOpacity2(): number {
    let numBonuses: number = 0
    if (this.collectorScore2) {
      if (this.collectorScore.challengeCompletionBonus) numBonuses++
      if (this.collectorScore.setBonus) numBonuses++
      if (this.collectorScore.seriesTeamBonus) numBonuses++
      if (this.collectorScore.fullTeamBonus) numBonuses++
      if (this.collectorScore2.historicFullTeamBonus) numBonuses++
      if (this.collectorScore2.badgeBonus) numBonuses++
      // if (this.collectorScore2.marketSpendBonus) numBonuses++
    }
    if (numBonuses === 0) return 0.5
    if (numBonuses === 1) return 0.75
    if (numBonuses >= 2) return 1
  }

  public getCollectionScoreHTML2(): string {
    if (!this.collectorScore2) return ''           // should never happen
    let html = `<div class="csLine"><b>Baseline:</b> <span class="csValue">${this.collectorScore2.baseline}</span></div>`
    if (this.collectorScore2.challengeCompletionBonus) html += `<div class="csLine"><b>Challenge Completion:</b> <span class="csValue">${this.collectorScore2.challengeCompletionBonus}</span></div>`
    if (this.collectorScore2.setBonus) html += `<div class="csLine"><b>Set Completion:</b> <span class="csValue">${this.collectorScore2.setBonus}</span></div>`
    if (this.collectorScore2.seriesTeamBonus) html += `<div class="csLine"><b>Series Team Completion:</b> <span class="csValue">${this.collectorScore2.seriesTeamBonus}</span></div>`
    if (this.collectorScore2.fullTeamBonus) html += `<div class="csLine"><b>Full Team Completion:</b> <span class="csValue">${this.collectorScore2.fullTeamBonus}</span></div>`
    if (this.collectorScore2.historicFullTeamBonus) html += `<div class="csLine"><b>Historic Full Team Completion:</b> <span class="csValue">${this.collectorScore2.historicFullTeamBonus}</span></div>`
    if (this.collectorScore2.badgeBonus) html += `<div class="csLine"><b>Badge Bonus:</b> <span class="csValue">${this.collectorScore2.badgeBonus}</span></div>`
    if (this.collectorScore2.marketSpendBonus) html += `<div class="csLine"><b>Marketplace Spend Bonus:</b> <span class="csValue">${this.collectorScore2.marketSpendBonus}</span></div>`
    return html
  }

  public getTotalCollectorScore2b(): number {
    if (!this.collectorScore2b) return 0            // should never happen
    return this.collectorScore2b.score
  }

  // unused
  public getCollectorScoreClassName2b(): string {
    return 'common'
  }

  public getCollectorScoreOpacity2b(): number {
    return 1
  }

  public getCollectionScoreHTML2b(): string {
    if (!this.collectorScore2b) return ''           // should never happen
    let usePurchasePrice: boolean = this.collectorScore2b.mpsScore && (!this.collectorScore2b.aspScore || !this.collectorScore2b.aspWhen || this.collectorScore2b.mpsScore >= this.collectorScore2b.aspScore)
    let useASP: boolean = this.collectorScore2b.aspScore && this.collectorScore2b.aspWhen && (!this.collectorScore2b.mpsScore || this.collectorScore2b.aspScore > this.collectorScore2b.mpsScore)

    let html = `<div class="csLine"><span class="${usePurchasePrice ? '': 'dimmedAndGray'}">✅</span> <b>Purchase Price:</b> <span class="csValue">$${this.collectorScore2b.mpsScore ? (this.collectorScore2b.mpsScore / 10).toFixed(2) : 0}</span></div>`
    if (!this.collectorScore2b.aspWhen) html += `<div class="csLine"><span class="${useASP ? '': 'invisible'}">✅</span> <b>Avg Sale Price:</b> <span class="csValue">$${this.collectorScore2b.aspScore ? (this.collectorScore2b.aspScore / 10).toFixed(2) : 0} <span class="tinyfont">ASP not yet usable</span></span></div>`
    else html += `<div class="csLine"><span class="${useASP ? '': 'dimmedAndGray'}">✅</span> <b>Avg Sale Price:</b> <span class="csValue">$${this.collectorScore2b.aspScore ? (this.collectorScore2b.aspScore / 10).toFixed(2) : 0} <span class="tinyfont">as of ${this.collectorScore2b.aspWhen.toLocaleDateString()}</span></span></div>`

    // explainer text
    html += '<br />TSS is based on either the <b>purchase price</b> or the prevailing <b>average sale price</b>, whichever is greater.'

    // historical asp text
    if (useASP && this.collectorScore2b.aspWhen <= ASP_SNAPSHOT_DATE3) {
      html += '<br /><br />Futhermore, any moment acquired before May 11, 2022 will use the "historical" average sale price as of May 11, 2022.'
    }

    return html
  }

  public getLockedPopupTextHTML(): string {
    if (!this.locked || !this.lockDate || !this.lockExpiryDate) return ''                 // should never happen
    const lockDate: string = this.lockDate.toLocaleDateString()
    const lockExpiryDate: string = this.lockExpiryDate.toLocaleDateString()

    let html = `<div class="lockLine"><b>Locked:</b> <span class="lockValue">${ lockDate }</span></div>`
    html += `<div class="lockLine"><b>Expires:</b> <span class="lockValue">${ lockExpiryDate }</span></div>`

    return html
  }


  public daysHeld(): number {
    if (!this.heldKeyDate) return 0
    const msHeld: number = new Date().getTime() - this.heldKeyDate.getTime()
    return msHeld / (86400 * 1000)
  }

  // TODO: return auction status so that MomentOwnedListComponent can show the right button
}

(async () => {
})();
